import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaTrash,
  FaSpinner,
  FaCheck,
  FaTimes,
  FaLock,
  FaIdCard,
  FaShieldAlt,
} from 'react-icons/fa';
import { useAuth } from '../auth/context/AuthContext';
import ConfirmationDialog from '../restaurants/ConfirmationDialog';

const OTP_EXPIRY_TIME = 300; // 5 minutes (in seconds)

const CustomerSettings = () => {
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
  });
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPhoneNumber, setNewPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_TIME);
  const [otpInterval, setOtpInterval] = useState(null);
  const { customerToken, customerLogout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        const response = await axios.get('/api/customers/profile', {
          headers: { Authorization: `Bearer ${customerToken}` },
        });
        setCustomerData(response.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        toast.error('Failed to load your information. Please try again.');
        setLoading(false);
      }
    };

    fetchCustomerData();
  }, [customerToken]);

  useEffect(() => {
    return () => {
      if (otpInterval) clearInterval(otpInterval);
    };
  }, [otpInterval]);

  const handleInputChange = (e) => {
    setCustomerData({ ...customerData, [e.target.name]: e.target.value });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const updateData = {
        name: customerData.name,
        email: customerData.email,
      };
      await axios.put('/api/customers/profile', updateData, {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(
        error.response?.data?.message ||
          'Failed to update profile. Please try again.'
      );
    } finally {
      setUpdating(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!customerData.email) {
      toast.error(
        'Please set your email address before updating your password.'
      );
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match.');
      return;
    }
    setUpdatingPassword(true);
    try {
      await axios.put(
        '/api/customers/update-password',
        {
          currentPassword,
          newPassword,
        },
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error(
        error.response?.data?.message ||
          'Failed to update password. Please try again.'
      );
    } finally {
      setUpdatingPassword(false);
    }
  };

  const startOtpTimer = () => {
    if (otpInterval) clearInterval(otpInterval);
    setOtpTimer(OTP_EXPIRY_TIME);
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev === 1) clearInterval(interval);
        return prev - 1;
      });
    }, 1000);
    setOtpInterval(interval);
  };

  const handleSendOtp = async () => {
    try {
      await axios.post(
        '/api/auth/customer/login',
        { phoneNumber: newPhoneNumber },
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      setOtpSent(true);
      startOtpTimer();
      toast.success('OTP sent successfully');
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Failed to send OTP. Please try again.');
    }
  };

  const handleUpdatePhoneNumber = async () => {
    try {
      await axios.post(
        '/api/auth/customer/verify-otp',
        {
          phoneNumber: newPhoneNumber,
          verificationCode: otp,
        },
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      await axios.put(
        '/api/customers/update-phone',
        { phoneNumber: newPhoneNumber },
        {
          headers: { Authorization: `Bearer ${customerToken}` },
        }
      );
      toast.success('Phone number updated successfully');
      setCustomerData({ ...customerData, phoneNumber: newPhoneNumber });
      setNewPhoneNumber('');
      setOtp('');
      setOtpSent(false);
      if (otpInterval) clearInterval(otpInterval);
    } catch (error) {
      console.error('Error updating phone number:', error);
      toast.error('Failed to update phone number. Please try again.');
    }
  };

  const handleCancelPhoneUpdate = () => {
    setNewPhoneNumber('');
    setOtp('');
    setOtpSent(false);
    if (otpInterval) clearInterval(otpInterval);
  };

  const handleDeleteAccount = async () => {
    try {
      await axios.delete('/api/customers/account', {
        headers: { Authorization: `Bearer ${customerToken}` },
      });
      customerLogout();
      toast.success('Your account has been deleted.');
      navigate('/');
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Failed to delete account. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className='flex justify-center items-center h-screen'>
        <FaSpinner className='animate-spin text-4xl text-blue-500' />
      </div>
    );
  }

  return (
    <div className='container mx-auto px-4 py-8 max-w-3xl'>
      <h1 className='text-4xl font-bold mb-8 text-center text-gray-800'>
        Account Settings
      </h1>
      <div className='bg-white shadow-2xl rounded-lg overflow-hidden'>
        <div className='p-8 space-y-8'>
          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-gray-700 flex items-center'>
              <FaIdCard className='mr-3' /> Profile Information
            </h2>
            <form onSubmit={handleUpdateProfile} className='space-y-6'>
              <div>
                <label
                  htmlFor='name'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Name (Optional)
                </label>
                <input
                  type='text'
                  id='name'
                  name='name'
                  value={customerData.name}
                  onChange={handleInputChange}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Your name'
                />
              </div>
              <div>
                <label
                  htmlFor='email'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Email
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={customerData.email}
                  onChange={handleInputChange}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Your email'
                />
              </div>
              <button
                type='submit'
                disabled={updating}
                className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200'
              >
                {updating ? (
                  <>
                    <FaSpinner className='animate-spin mr-2' />
                    Updating...
                  </>
                ) : (
                  <>
                    <FaCheck className='mr-2' />
                    Update Profile
                  </>
                )}
              </button>
            </form>
          </div>

          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-gray-700 flex items-center'>
              <FaLock className='mr-3' /> Update Password
            </h2>
            <form onSubmit={handleUpdatePassword} className='space-y-6'>
              <div>
                <label
                  htmlFor='currentPassword'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Current Password
                </label>
                <input
                  type='password'
                  id='currentPassword'
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Enter current password'
                />
              </div>
              <div>
                <label
                  htmlFor='newPassword'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  New Password
                </label>
                <input
                  type='password'
                  id='newPassword'
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Enter new password'
                />
              </div>
              <div>
                <label
                  htmlFor='confirmPassword'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Confirm New Password
                </label>
                <input
                  type='password'
                  id='confirmPassword'
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Confirm new password'
                />
              </div>
              <button
                type='submit'
                disabled={updatingPassword}
                className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-200'
              >
                {updatingPassword ? (
                  <>
                    <FaSpinner className='animate-spin mr-2' />
                    Updating Password...
                  </>
                ) : (
                  <>
                    <FaCheck className='mr-2' />
                    Update Password
                  </>
                )}
              </button>
            </form>
          </div>

          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-gray-700 flex items-center'>
              <FaPhone className='mr-3' /> Phone Number
            </h2>
            <div>
              <label
                htmlFor='currentPhoneNumber'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                Current Phone Number
              </label>
              <input
                type='tel'
                id='currentPhoneNumber'
                value={customerData.phoneNumber}
                disabled
                className='w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-100'
              />
            </div>
            <div>
              <label
                htmlFor='newPhoneNumber'
                className='block text-sm font-medium text-gray-700 mb-1'
              >
                New Phone Number
              </label>
              <input
                type='tel'
                id='newPhoneNumber'
                value={newPhoneNumber}
                onChange={(e) => setNewPhoneNumber(e.target.value)}
                className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                placeholder='Enter new phone number'
              />
            </div>
            {otpSent && (
              <div>
                <label
                  htmlFor='otp'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  OTP
                </label>
                <input
                  type='text'
                  id='otp'
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className='w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200'
                  placeholder='Enter OTP'
                />
                <p className='mt-2 text-sm text-gray-600'>
                  OTP expires in {otpTimer} seconds
                </p>
              </div>
            )}
            <div className='flex space-x-4'>
              <button
                onClick={otpSent ? handleUpdatePhoneNumber : handleSendOtp}
                disabled={!newPhoneNumber}
                className='flex-1 flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-200'
              >
                {otpSent ? 'Verify OTP & Update' : 'Send OTP'}
              </button>
              {otpSent && (
                <button
                  onClick={handleSendOtp}
                  className='flex-1 flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition duration-200'
                >
                  Resend OTP
                </button>
              )}
            </div>
            {otpSent && (
              <button
                onClick={handleCancelPhoneUpdate}
                className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition duration-200'
              >
                <FaTimes className='mr-2' />
                Cancel Phone Update
              </button>
            )}
          </div>

          <div className='space-y-6'>
            <h2 className='text-2xl font-semibold text-red-600 flex items-center'>
              <FaTrash className='mr-3' /> Danger Zone
            </h2>
            <p className='text-gray-600'>
              Once you delete your account, there is no going back. Please be
              certain.
            </p>
            <button
              onClick={() => setShowDeleteConfirmation(true)}
              className='w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-base font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition duration-200'
            >
              <FaTrash className='mr-2' />
              Delete Account
            </button>
          </div>
        </div>
      </div>
      <ConfirmationDialog
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeleteAccount}
        title='Delete Account'
        message='Are you sure you want to delete your account? This action cannot be undone.'
      />
    </div>
  );
};

export default CustomerSettings;
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { toast } from 'react-toastify';
// import {
//   FaUser,
//   FaEnvelope,
//   FaPhone,
//   FaTrash,
//   FaSpinner,
//   FaCheck,
//   FaTimes,
//   FaLock,
// } from 'react-icons/fa';
// import { useAuth } from '../auth/context/AuthContext';
// import ConfirmationDialog from '../restaurants/ConfirmationDialog';

// const OTP_EXPIRY_TIME = 300; // 5 minutes (in seconds)

// const CustomerSettings = () => {
//   const [customerData, setCustomerData] = useState({
//     name: '',
//     recoveryEmail: '',
//     phoneNumber: '',
//   });
//   //   const [password, setPassword] = useState('');
//   //   const [confirmPassword, setConfirmPassword] = useState('');
//   const [newPhoneNumber, setNewPhoneNumber] = useState('');
//   const [otp, setOtp] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [updating, setUpdating] = useState(false);
//   const [updatingPhone, setUpdatingPhone] = useState(false);
//   const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
//   const [phoneUpdateStep, setPhoneUpdateStep] = useState('edit'); // 'edit', 'verify', 'update'
//   const [otpTimer, setOtpTimer] = useState(OTP_EXPIRY_TIME);
//   const [otpInterval, setOtpInterval] = useState(null);
//   const { customerToken, customerLogout } = useAuth();
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchCustomerData = async () => {
//       try {
//         const response = await axios.get('/api/customers/profile', {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//         setCustomerData(response.data);
//         setLoading(false);
//       } catch (error) {
//         console.error('Error fetching customer data:', error);
//         toast.error('Failed to load your information. Please try again.');
//         setLoading(false);
//       }
//     };

//     fetchCustomerData();
//   }, [customerToken]);

//   useEffect(() => {
//     return () => {
//       if (otpInterval) clearInterval(otpInterval);
//     };
//   }, [otpInterval]);

//   const handleInputChange = (e) => {
//     setCustomerData({ ...customerData, [e.target.name]: e.target.value });
//   };

//   const handleUpdateProfile = async (e) => {
//     e.preventDefault();
//     setUpdating(true);
//     try {
//       //   if (password !== confirmPassword) {
//       //     throw new Error("Passwords don't match");
//       //   }
//       const updateData = {
//         name: customerData.name,
//         recoveryEmail: customerData.recoveryEmail,
//       };
//       //   if (password) {
//       //     updateData.password = password;
//       //   }
//       await axios.put('/api/customers/profile', updateData, {
//         headers: { Authorization: `Bearer ${customerToken}` },
//       });
//       toast.success('Profile updated successfully');
//       //   setPassword('');
//       //   setConfirmPassword('');
//     } catch (error) {
//       console.error('Error updating profile:', error);
//       toast.error(
//         error.message || 'Failed to update profile. Please try again.'
//       );
//     } finally {
//       setUpdating(false);
//     }
//   };

//   const startOtpTimer = () => {
//     if (otpInterval) clearInterval(otpInterval);
//     setOtpTimer(OTP_EXPIRY_TIME);
//     const interval = setInterval(() => {
//       setOtpTimer((prev) => {
//         if (prev === 1) clearInterval(interval);
//         return prev - 1;
//       });
//     }, 1000);
//     setOtpInterval(interval);
//   };

//   const handleSendOtp = async () => {
//     try {
//       await axios.post(
//         '/api/auth/customer/login',
//         { phoneNumber: newPhoneNumber },
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       setPhoneUpdateStep('verify');
//       startOtpTimer();
//       toast.success('OTP sent successfully');
//     } catch (error) {
//       console.error('Error sending OTP:', error);
//       toast.error('Failed to send OTP. Please try again.');
//     }
//   };

//   const handleVerifyOtp = async () => {
//     try {
//       await axios.post(
//         '/api/auth/customer/verify-otp',
//         {
//           phoneNumber: newPhoneNumber,
//           verificationCode: otp,
//         },
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       toast.success('OTP verified successfully');
//       setPhoneUpdateStep('update');
//     } catch (error) {
//       console.error('Error verifying OTP:', error);
//       toast.error('Invalid OTP. Please try again.');
//     }
//   };

//   const handleUpdatePhoneNumber = async () => {
//     setUpdatingPhone(true);
//     try {
//       await axios.put(
//         '/api/customers/update-phone',
//         { phoneNumber: newPhoneNumber },
//         {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         }
//       );
//       toast.success('Phone number updated successfully');
//       setCustomerData({ ...customerData, phoneNumber: newPhoneNumber });
//       setPhoneUpdateStep('edit');
//       setNewPhoneNumber('');
//       setOtp('');
//     } catch (error) {
//       console.error('Error updating phone number:', error);
//       toast.error('Failed to update phone number. Please try again.');
//     } finally {
//       setUpdatingPhone(false);
//     }
//   };

//   const handleCancelPhoneUpdate = () => {
//     setPhoneUpdateStep('edit');
//     setNewPhoneNumber('');
//     setOtp('');
//     if (otpInterval) clearInterval(otpInterval);
//   };

//   const handleDeleteAccount = async () => {
//     try {
//       await axios.delete('/api/customers/account', {
//         headers: { Authorization: `Bearer ${customerToken}` },
//       });
//       customerLogout();
//       toast.success('Your account has been deleted.');
//       navigate('/');
//     } catch (error) {
//       console.error('Error deleting account:', error);
//       toast.error('Failed to delete account. Please try again.');
//     }
//   };

//   if (loading) {
//     return (
//       <div className='flex justify-center items-center h-screen'>
//         <FaSpinner className='animate-spin text-4xl text-blue-500' />
//       </div>
//     );
//   }

//   return (
//     <div className='container mx-auto px-4 py-8 max-w-2xl'>
//       <h1 className='text-3xl font-bold mb-6 text-center'>Account Settings</h1>
//       <div className='bg-white shadow-lg rounded-lg overflow-hidden mb-6'>
//         <div className='p-6 space-y-6'>
//           <h2 className='text-xl font-semibold'>Personal Information</h2>
//           <form onSubmit={handleUpdateProfile} className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='name'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Name
//               </label>
//               <input
//                 type='text'
//                 id='name'
//                 name='name'
//                 value={customerData.name}
//                 onChange={handleInputChange}
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//               />
//             </div>
//             <div>
//               <label
//                 htmlFor='recoveryEmail'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Recovery Email
//               </label>
//               <input
//                 type='email'
//                 id='recoveryEmail'
//                 name='recoveryEmail'
//                 value={customerData.recoveryEmail}
//                 onChange={handleInputChange}
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//               />
//             </div>
//             {/* <div>
//               <label
//                 htmlFor='password'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 New Password (Optional)
//               </label>
//               <input
//                 type='password'
//                 id='password'
//                 value={password}
//                 onChange={(e) => setPassword(e.target.value)}
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//               />
//             </div>
//             <div>
//               <label
//                 htmlFor='confirmPassword'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Confirm New Password
//               </label>
//               <input
//                 type='password'
//                 id='confirmPassword'
//                 value={confirmPassword}
//                 onChange={(e) => setConfirmPassword(e.target.value)}
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//               />
//             </div> */}
//             <button
//               type='submit'
//               disabled={updating}
//               className='w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-300 flex items-center justify-center'
//             >
//               {updating ? (
//                 <>
//                   <FaSpinner className='animate-spin mr-2' />
//                   Updating...
//                 </>
//               ) : (
//                 <>
//                   <FaCheck className='mr-2' />
//                   Update Profile
//                 </>
//               )}
//             </button>
//           </form>
//         </div>
//       </div>

//       <div className='bg-white shadow-lg rounded-lg overflow-hidden mb-6'>
//         <div className='p-6 space-y-6'>
//           <h2 className='text-xl font-semibold'>Phone Number</h2>
//           <div className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='currentPhoneNumber'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Current Phone Number
//               </label>
//               <input
//                 type='tel'
//                 id='currentPhoneNumber'
//                 value={customerData.phoneNumber}
//                 disabled
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100'
//               />
//             </div>
//             {phoneUpdateStep === 'edit' && (
//               <div>
//                 <label
//                   htmlFor='newPhoneNumber'
//                   className='block text-sm font-medium text-gray-700'
//                 >
//                   New Phone Number
//                 </label>
//                 <input
//                   type='tel'
//                   id='newPhoneNumber'
//                   value={newPhoneNumber}
//                   onChange={(e) => setNewPhoneNumber(e.target.value)}
//                   className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                 />
//                 <button
//                   onClick={handleSendOtp}
//                   disabled={!newPhoneNumber}
//                   className='mt-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300 w-full'
//                 >
//                   Send OTP
//                 </button>
//               </div>
//             )}
//             {phoneUpdateStep === 'verify' && (
//               <div>
//                 <label
//                   htmlFor='otp'
//                   className='block text-sm font-medium text-gray-700'
//                 >
//                   OTP
//                 </label>
//                 <input
//                   type='text'
//                   id='otp'
//                   value={otp}
//                   onChange={(e) => setOtp(e.target.value)}
//                   className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                   placeholder='Enter OTP'
//                 />
//                 <button
//                   onClick={handleVerifyOtp}
//                   className='mt-2 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition duration-300 w-full'
//                 >
//                   Verify OTP
//                 </button>
//                 <p className='mt-2 text-sm text-gray-600'>
//                   OTP expires in {otpTimer} seconds
//                 </p>
//                 <button
//                   onClick={handleSendOtp}
//                   className='mt-2 bg-indigo-500 text-white px-4 py-2 rounded-md hover:bg-indigo-600 transition duration-300 w-full'
//                 >
//                   Resend OTP
//                 </button>
//               </div>
//             )}
//             {phoneUpdateStep === 'update' && (
//               <button
//                 onClick={handleUpdatePhoneNumber}
//                 disabled={updatingPhone}
//                 className='w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-300 flex items-center justify-center'
//               >
//                 {updatingPhone ? (
//                   <>
//                     <FaSpinner className='animate-spin mr-2' />
//                     Updating...
//                   </>
//                 ) : (
//                   <>
//                     <FaCheck className='mr-2' />
//                     Update Phone Number
//                   </>
//                 )}
//               </button>
//             )}
//             {phoneUpdateStep !== 'edit' && (
//               <button
//                 onClick={handleCancelPhoneUpdate}
//                 className='w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition duration-300 flex items-center justify-center mt-2'
//               >
//                 <FaTimes className='mr-2' />
//                 Cancel
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       <div className='bg-white shadow-lg rounded-lg overflow-hidden'>
//         <div className='p-6'>
//           <h2 className='text-xl font-semibold mb-4'>Danger Zone</h2>
//           <button
//             onClick={() => setShowDeleteConfirmation(true)}
//             className='w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition duration-300 flex items-center justify-center'
//           >
//             <FaTrash className='mr-2' />
//             Delete Account
//           </button>
//         </div>
//       </div>
//       <ConfirmationDialog
//         isOpen={showDeleteConfirmation}
//         onClose={() => setShowDeleteConfirmation(false)}
//         onConfirm={handleDeleteAccount}
//         title='Delete Account'
//         message='Are you sure you want to delete your account? This action cannot be undone.'
//       />
//     </div>
//   );
// };

// export default CustomerSettings;
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { toast } from 'react-toastify';
// import {
//   FaUser,
//   FaEnvelope,
//   FaPhone,
//   FaTrash,
//   FaSpinner,
//   FaCheck,
//   FaTimes,
// } from 'react-icons/fa';
// import { useAuth } from '../auth/context/AuthContext';
// import ConfirmationDialog from '../restaurants/ConfirmationDialog';

// const CustomerSettings = () => {
//   const [customerData, setCustomerData] = useState({
//     name: '',
//     recoveryEmail: '',
//     phoneNumber: '',
//   });
//   const [newPhoneNumber, setNewPhoneNumber] = useState('');
//   const [otp, setOtp] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [updating, setUpdating] = useState(false);
//   const [updatingPhone, setUpdatingPhone] = useState(false);
//   const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
//   const [phoneUpdateStep, setPhoneUpdateStep] = useState('edit'); // 'edit', 'verify', 'update'
//   const [otpSent, setOtpSent] = useState(false);
//   const { customerToken, customerLogout } = useAuth();
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchCustomerData = async () => {
//       try {
//         const response = await axios.get('/api/customers/profile', {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//         setCustomerData(response.data);
//         setLoading(false);
//       } catch (error) {
//         console.error('Error fetching customer data:', error);
//         toast.error('Failed to load your information. Please try again.');
//         setLoading(false);
//       }
//     };

//     fetchCustomerData();
//   }, [customerToken]);

//   const handleInputChange = (e) => {
//     setCustomerData({ ...customerData, [e.target.name]: e.target.value });
//   };

//   const handleUpdateProfile = async (e) => {
//     e.preventDefault();
//     setUpdating(true);
//     try {
//       await axios.put(
//         '/api/customers/profile',
//         { name: customerData.name, recoveryEmail: customerData.recoveryEmail },
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       toast.success('Profile updated successfully');
//     } catch (error) {
//       console.error('Error updating profile:', error);
//       toast.error('Failed to update profile. Please try again.');
//     } finally {
//       setUpdating(false);
//     }
//   };

//   const handleSendOtp = async () => {
//     try {
//       //   await axios.post(
//       //     '/api/customers/send-otp',
//       //     { phoneNumber: newPhoneNumber },
//       //     { headers: { Authorization: `Bearer ${customerToken}` } }
//       //   );
//       await axios.post(
//         '/api/auth/customer/login',
//         { phoneNumber: newPhoneNumber },
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );

//       setOtpSent(true);
//       toast.success('OTP sent successfully');
//       setPhoneUpdateStep('verify');
//     } catch (error) {
//       console.error('Error sending OTP:', error);
//       toast.error('Failed to send OTP. Please try again.');
//     }
//   };

//   const handleVerifyOtp = async () => {
//     try {
//       //   await axios.post(
//       //     '/api/customers/verify-otp',
//       //     { otp, phoneNumber: newPhoneNumber },
//       //     { headers: { Authorization: `Bearer ${customerToken}` } }
//       //   );
//       const response = await axios.post(
//         '/api/auth/customer/verify-otp',
//         {
//           phoneNumber: newPhoneNumber,
//           verificationCode: otp,
//         },
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       toast.success('OTP verified successfully');
//       setPhoneUpdateStep('update');
//     } catch (error) {
//       console.error('Error verifying OTP:', error);
//       toast.error('Invalid OTP. Please try again.');
//     }
//   };

//   const handleUpdatePhoneNumber = async () => {
//     setUpdatingPhone(true);
//     try {
//       await axios.put(
//         '/api/customers/update-phone',
//         { phoneNumber: newPhoneNumber },
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       toast.success('Phone number updated successfully');
//       setCustomerData({ ...customerData, phoneNumber: newPhoneNumber });
//       setPhoneUpdateStep('edit');
//       setNewPhoneNumber('');
//       setOtp('');
//       setOtpSent(false);
//     } catch (error) {
//       console.error('Error updating phone number:', error);
//       toast.error('Failed to update phone number. Please try again.');
//     } finally {
//       setUpdatingPhone(false);
//     }
//   };

//   const handleCancelPhoneUpdate = () => {
//     setPhoneUpdateStep('edit');
//     setNewPhoneNumber('');
//     setOtp('');
//     setOtpSent(false);
//   };

//   const handleDeleteAccount = async () => {
//     try {
//       await axios.delete('/api/customers/account', {
//         headers: { Authorization: `Bearer ${customerToken}` },
//       });
//       customerLogout();
//       toast.success('Your account has been deleted.');
//       navigate('/');
//     } catch (error) {
//       console.error('Error deleting account:', error);
//       toast.error('Failed to delete account. Please try again.');
//     }
//   };

//   if (loading) {
//     return (
//       <div className='flex justify-center items-center h-screen'>
//         <FaSpinner className='animate-spin text-4xl text-blue-500' />
//       </div>
//     );
//   }

//   return (
//     <div className='container mx-auto px-4 py-8 max-w-2xl'>
//       <h1 className='text-3xl font-bold mb-6 text-center'>Account Settings</h1>
//       <div className='bg-white shadow-lg rounded-lg overflow-hidden mb-6'>
//         <div className='p-6 space-y-6'>
//           <h2 className='text-xl font-semibold'>Personal Information</h2>
//           <form onSubmit={handleUpdateProfile} className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='name'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Name
//               </label>
//               <input
//                 type='text'
//                 id='name'
//                 name='name'
//                 value={customerData.name}
//                 onChange={handleInputChange}
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//               />
//             </div>
//             <div>
//               <label
//                 htmlFor='recoveryEmail'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Recovery Email
//               </label>
//               <input
//                 type='email'
//                 id='recoveryEmail'
//                 name='recoveryEmail'
//                 value={customerData.recoveryEmail}
//                 onChange={handleInputChange}
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//               />
//             </div>
//             <button
//               type='submit'
//               disabled={updating}
//               className='w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-300 flex items-center justify-center'
//             >
//               {updating ? (
//                 <>
//                   <FaSpinner className='animate-spin mr-2' />
//                   Updating...
//                 </>
//               ) : (
//                 <>
//                   <FaCheck className='mr-2' />
//                   Update Profile
//                 </>
//               )}
//             </button>
//           </form>
//         </div>
//       </div>

//       <div className='bg-white shadow-lg rounded-lg overflow-hidden mb-6'>
//         <div className='p-6 space-y-6'>
//           <h2 className='text-xl font-semibold'>Phone Number</h2>
//           <div className='space-y-4'>
//             <div>
//               <label
//                 htmlFor='currentPhoneNumber'
//                 className='block text-sm font-medium text-gray-700'
//               >
//                 Current Phone Number
//               </label>
//               <input
//                 type='tel'
//                 id='currentPhoneNumber'
//                 value={customerData.phoneNumber}
//                 disabled
//                 className='mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100'
//               />
//             </div>
//             {phoneUpdateStep === 'edit' && (
//               <div>
//                 <label
//                   htmlFor='newPhoneNumber'
//                   className='block text-sm font-medium text-gray-700'
//                 >
//                   New Phone Number
//                 </label>
//                 <input
//                   type='tel'
//                   id='newPhoneNumber'
//                   value={newPhoneNumber}
//                   onChange={(e) => setNewPhoneNumber(e.target.value)}
//                   className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                 />
//                 <button
//                   onClick={handleSendOtp}
//                   disabled={!newPhoneNumber}
//                   className='mt-2 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition duration-300 w-full'
//                 >
//                   Send OTP
//                 </button>
//               </div>
//             )}
//             {phoneUpdateStep === 'verify' && (
//               <div>
//                 <label
//                   htmlFor='otp'
//                   className='block text-sm font-medium text-gray-700'
//                 >
//                   OTP
//                 </label>
//                 <input
//                   type='text'
//                   id='otp'
//                   value={otp}
//                   onChange={(e) => setOtp(e.target.value)}
//                   className='mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50'
//                   placeholder='Enter OTP'
//                 />
//                 <button
//                   onClick={handleVerifyOtp}
//                   className='mt-2 bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition duration-300 w-full'
//                 >
//                   Verify OTP
//                 </button>
//               </div>
//             )}
//             {phoneUpdateStep === 'update' && (
//               <button
//                 onClick={handleUpdatePhoneNumber}
//                 disabled={updatingPhone}
//                 className='w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-300 flex items-center justify-center'
//               >
//                 {updatingPhone ? (
//                   <>
//                     <FaSpinner className='animate-spin mr-2' />
//                     Updating...
//                   </>
//                 ) : (
//                   <>
//                     <FaCheck className='mr-2' />
//                     Update Phone Number
//                   </>
//                 )}
//               </button>
//             )}
//             {phoneUpdateStep !== 'edit' && (
//               <button
//                 onClick={handleCancelPhoneUpdate}
//                 className='w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400 transition duration-300 flex items-center justify-center mt-2'
//               >
//                 <FaTimes className='mr-2' />
//                 Cancel Phone Update
//               </button>
//             )}
//           </div>
//         </div>
//       </div>

//       <div className='bg-white shadow-lg rounded-lg overflow-hidden'>
//         <div className='p-6'>
//           <h2 className='text-xl font-semibold mb-4'>Danger Zone</h2>
//           <button
//             onClick={() => setShowDeleteConfirmation(true)}
//             className='w-full bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition duration-300 flex items-center justify-center'
//           >
//             <FaTrash className='mr-2' />
//             Delete Account
//           </button>
//         </div>
//       </div>
//       <ConfirmationDialog
//         isOpen={showDeleteConfirmation}
//         onClose={() => setShowDeleteConfirmation(false)}
//         onConfirm={handleDeleteAccount}
//         title='Delete Account'
//         message='Are you sure you want to delete your account? This action cannot be undone.'
//       />
//     </div>
//   );
// };

// export default CustomerSettings;
// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import { toast } from 'react-toastify';
// import { FaUser, FaEnvelope, FaTrash, FaSpinner } from 'react-icons/fa';
// import { useAuth } from '../auth/context/AuthContext';
// import ConfirmationDialog from '../restaurants/ConfirmationDialog';

// const CustomerSettings = () => {
//   const [name, setName] = useState('');
//   const [recoveryEmail, setRecoveryEmail] = useState('');
//   const [loading, setLoading] = useState(true);
//   const [updating, setUpdating] = useState(false);
//   const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
//   const { customerToken, customerLogout } = useAuth();
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchCustomerData = async () => {
//       try {
//         const response = await axios.get('/api/customers/profile', {
//           headers: { Authorization: `Bearer ${customerToken}` },
//         });
//         setName(response.data.name || '');
//         setRecoveryEmail(response.data.recoveryEmail || '');
//         setLoading(false);
//       } catch (error) {
//         console.error('Error fetching customer data:', error);
//         toast.error('Failed to load your information. Please try again.');
//         setLoading(false);
//       }
//     };

//     fetchCustomerData();
//   }, [customerToken]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setUpdating(true);
//     try {
//       await axios.put(
//         '/api/customers/profile',
//         { name, recoveryEmail },
//         { headers: { Authorization: `Bearer ${customerToken}` } }
//       );
//       toast.success('Profile updated successfully');
//     } catch (error) {
//       console.error('Error updating profile:', error);
//       toast.error('Failed to update profile. Please try again.');
//     } finally {
//       setUpdating(false);
//     }
//   };

//   const handleDeleteAccount = async () => {
//     try {
//       await axios.delete('/api/customers/account', {
//         headers: { Authorization: `Bearer ${customerToken}` },
//       });
//       customerLogout();
//       toast.success('Your account has been deleted.');
//       navigate('/');
//     } catch (error) {
//       console.error('Error deleting account:', error);
//       toast.error('Failed to delete account. Please try again.');
//     }
//   };

//   if (loading) {
//     return (
//       <div className='flex justify-center items-center h-screen'>
//         <FaSpinner className='animate-spin text-4xl text-blue-500' />
//       </div>
//     );
//   }

//   return (
//     <div className='container mx-auto px-4 py-8 max-w-md'>
//       <h1 className='text-3xl font-bold mb-6'>Account Settings</h1>
//       <form onSubmit={handleSubmit} className='space-y-6'>
//         <div>
//           <label
//             htmlFor='name'
//             className='block text-sm font-medium text-gray-700'
//           >
//             Name (Optional)
//           </label>
//           <div className='mt-1 relative rounded-md shadow-sm'>
//             <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
//               <FaUser className='text-gray-400' />
//             </div>
//             <input
//               type='text'
//               id='name'
//               value={name}
//               onChange={(e) => setName(e.target.value)}
//               className='focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md'
//               placeholder='Your name'
//             />
//           </div>
//         </div>
//         <div>
//           <label
//             htmlFor='recoveryEmail'
//             className='block text-sm font-medium text-gray-700'
//           >
//             Recovery Email (Optional)
//           </label>
//           <div className='mt-1 relative rounded-md shadow-sm'>
//             <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
//               <FaEnvelope className='text-gray-400' />
//             </div>
//             <input
//               type='email'
//               id='recoveryEmail'
//               value={recoveryEmail}
//               onChange={(e) => setRecoveryEmail(e.target.value)}
//               className='focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md'
//               placeholder='recovery@example.com'
//             />
//           </div>
//         </div>
//         <button
//           type='submit'
//           disabled={updating}
//           className='w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
//         >
//           {updating ? (
//             <>
//               <FaSpinner className='animate-spin mr-2' />
//               Updating...
//             </>
//           ) : (
//             'Update Profile'
//           )}
//         </button>
//       </form>
//       <div className='mt-8'>
//         <h2 className='text-xl font-bold mb-4'>Danger Zone</h2>
//         <button
//           onClick={() => setShowDeleteConfirmation(true)}
//           className='w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
//         >
//           <FaTrash className='mr-2' />
//           Delete Account
//         </button>
//       </div>
//       <ConfirmationDialog
//         isOpen={showDeleteConfirmation}
//         onClose={() => setShowDeleteConfirmation(false)}
//         onConfirm={handleDeleteAccount}
//         title='Delete Account'
//         message='Are you sure you want to delete your account? This action cannot be undone.'
//       />
//     </div>
//   );
// };

// export default CustomerSettings;
